import { useEffect, useState } from "react";
import { Modal, Table, Button, Tag, Input as AntdInput } from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import trans from "../../../utils";
import { Input, InputDate } from "../../Helpers/Input/Input";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import { useTechnicians } from "../../../hooks/useTechnicians";
import { useGetServiceRequestsQuery } from "../../../Redux/service/Dispatcher/Dispatcher";
import { useCreateDispatchPlanMutation } from "../../../Redux/service/Dispatcher/DispatcherPlan";
import { useGetAllSkusQuery } from "../../../Redux/service/Sku/Sku";
import { EgyptGovernorates } from "../../../constants";
import { getMarkazOptions } from "../../../utils/CountryState/egyptMarkaz";
import { formatDateString } from "../../../JsHelpers/DateFormat";

const DISPATCH_STATUS_COLORS = {
	open: "default",
	assigned: "purple",
	pending: "orange",
	inProgress: "blue",
	completed: "green",
	closed: "cyan",
	cancelled: "red",
};

const SORT_FIELD_BY_COLUMN_KEY = {
	requestNumber: "requestNumber",
	preferredVisitTime: "preferredVisitTime",
	requestedDate: "requestedDate",
};

function arraysEqual(a = [], b = []) {
	if (a.length !== b.length) return false;
	const sortedA = [...a].sort();
	const sortedB = [...b].sort();
	return sortedA.every((v, i) => v === sortedB[i]);
}

const DEFAULT_TOUCHED = {
	planName: false,
	assigneeId: false,
	planningDate: false,
};

export default function NewDispatchPlanModal({ open, onClose, onCreate }) {
	const [planName, setPlanName] = useState("");
	const [assigneeId, setAssigneeId] = useState(undefined);
	const [planningDate, setPlanningDate] = useState("");
	const [selectedIds, setSelectedIds] = useState([]);
	const [touched, setTouched] = useState(DEFAULT_TOUCHED);

	const [search, setSearch] = useState("");
	const [governorate, setGovernorate] = useState([]);
	const [markaz, setMarkaz] = useState([]);
	const [productId, setProductId] = useState([]);
	const [sort, setSort] = useState({
		sortBy: "requestedDate",
		sortOrder: "desc",
	});
	const [activeFilterKey, setActiveFilterKey] = useState(null);

	const { options: assigneeOptions, isFetching: assigneesLoading } =
		useTechnicians();

	const [createDispatchPlan, { isLoading: creating }] =
		useCreateDispatchPlanMutation();

	const { data: skusData } = useGetAllSkusQuery();
	const productOptions = (skusData?.data || []).map((s) => ({
		label: s.skuName,
		value: String(s.id),
	}));
	const markazOptions = governorate.length
		? Array.from(
				new Map(
					governorate
						.flatMap((g) => getMarkazOptions(g))
						.map((o) => [o.value, o]),
				).values(),
			)
		: [];

	const { data, isFetching: requestsLoading } = useGetServiceRequestsQuery(
		{
			status: "open",
			page: 1,
			limit: 25,
			search: search || undefined,
			sortBy: sort.sortBy,
			sortOrder: sort.sortOrder,
			governorate: governorate.length ? governorate.join(",") : undefined,
			markaz: markaz.length ? markaz.join(",") : undefined,
			productId: productId.length ? productId.join(",") : undefined,
		},
		{ skip: !open, refetchOnMountOrArgChange: true },
	);
	const requests = data?.data || [];

	useEffect(() => {
		if (open) {
			setPlanName("");
			setAssigneeId(undefined);
			setPlanningDate("");
			setSelectedIds([]);
			setTouched(DEFAULT_TOUCHED);
			setSearch("");
			setGovernorate([]);
			setMarkaz([]);
			setProductId([]);
			setSort({ sortBy: "requestedDate", sortOrder: "desc" });
		}
	}, [open]);

	const isPlanNameInvalid = !planName.trim();
	const isAssigneeInvalid = !assigneeId;
	const isPlanningDateInvalid = !planningDate;

	const handleCreate = async () => {
		setTouched({ planName: true, assigneeId: true, planningDate: true });
		if (
			isPlanNameInvalid ||
			isAssigneeInvalid ||
			isPlanningDateInvalid ||
			!selectedIds.length
		) {
			toast.error(
				trans("Please fill in all fields and select at least one request"),
				{ autoClose: 2500 },
			);
			return;
		}

		const payload = {
			name: planName.trim(),
			plannedDate: dayjs(planningDate).format("YYYY-MM-DD"),
			assignedToId: Number(assigneeId),
			serviceRequestIds: selectedIds,
		};

		try {
			const result = await createDispatchPlan(payload).unwrap();
			toast.success(trans("Dispatch plan created successfully"), {
				autoClose: 2000,
			});
			if (onCreate) {
				onCreate(result);
			}
			onClose();
		} catch (error) {
			toast.error(
				trans(error?.data?.message) || trans("Failed to create dispatch plan"),
				{ autoClose: 3000 },
			);
		}
	};

	const handleTableChange = (pagination, tableFilters, sorter) => {
		const nextSort = sorter.order
			? {
					sortBy: SORT_FIELD_BY_COLUMN_KEY[sorter.field] || "requestedDate",
					sortOrder: sorter.order === "ascend" ? "asc" : "desc",
				}
			: { sortBy: "requestedDate", sortOrder: "desc" };
		setSort(nextSort);

		const newGovernorate =
			activeFilterKey === "governorate"
				? tableFilters?.governorate || []
				: governorate;
		const governorateChanged = !arraysEqual(newGovernorate, governorate);
		const newMarkaz = governorateChanged
			? []
			: activeFilterKey === "markaz"
				? tableFilters?.markaz || []
				: markaz;
		const newProductId =
			activeFilterKey === "product" ? tableFilters?.product || [] : productId;

		setGovernorate(newGovernorate);
		setMarkaz(newMarkaz);
		setProductId(newProductId);
	};

	const columns = [
		{
			title: trans("Request ID"),
			dataIndex: "requestNumber",
			key: "requestNumber",
			width: 150,
			sorter: true,
		},
		{
			title: trans("Status"),
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (v) => (
				<Tag
					color={DISPATCH_STATUS_COLORS[v] || "default"}
					className="text-capitalize"
				>
					{trans(v)}
				</Tag>
			),
		},
		{
			title: trans("Government"),
			dataIndex: "governorate",
			key: "governorate",
			width: 180,
			filters: EgyptGovernorates.map((g) => ({
				text: g.label,
				value: g.value,
			})),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: governorate.length ? governorate : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (isOpen) => {
				if (isOpen) setActiveFilterKey("governorate");
			},
			render: (v) => (v ? trans(v) : "--"),
		},
		{
			title: trans("Markaz"),
			dataIndex: "markaz",
			key: "markaz",
			width: 140,
			filters: markazOptions.length
				? markazOptions.map((o) => ({ text: o.label, value: o.value }))
				: undefined,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: markaz.length ? markaz : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (isOpen) => {
				if (isOpen) setActiveFilterKey("markaz");
			},
			render: (v) => (v ? trans(v) : "--"),
		},
		{
			title: trans("Village"),
			dataIndex: "village",
			key: "village",
			width: 150,
			render: (v) => v || "--",
		},
		{
			title: trans("Address"),
			dataIndex: "address",
			key: "address",
			width: 200,
			ellipsis: true,
			render: (v) => v || "--",
		},
		{
			title: trans("Product"),
			dataIndex: "product",
			key: "product",
			width: 150,
			filters: productOptions.map((o) => ({ text: o.label, value: o.value })),
			filterSearch: true,
			filterMultiple: true,
			filteredValue: productId.length ? productId : null,
			onFilter: () => true,
			onFilterDropdownOpenChange: (isOpen) => {
				if (isOpen) setActiveFilterKey("product");
			},
			render: (v) => v || "--",
		},
		{
			title: trans("Model"),
			dataIndex: "model",
			key: "model",
			width: 140,
			render: (v) => v || "--",
		},
		{
			title: trans("Complaint Summary"),
			dataIndex: "complaintSummary",
			key: "complaintSummary",
			width: 220,
			ellipsis: true,
			render: (v) => v || "--",
		},
		{
			title: trans("Preferred Visit Time"),
			key: "preferredVisitTime",
			width: 260,
			sorter: true,
			render: (_, record) => {
				if (!record.preferredVisitTime) return "--";
				return record.preferredVisitTimeEnd
					? `${formatDateString(record.preferredVisitTime)} - ${formatDateString(record.preferredVisitTimeEnd)}`
					: formatDateString(record.preferredVisitTime);
			},
		},
		{
			title: trans("Requested Date"),
			dataIndex: "requestedDate",
			key: "requestedDate",
			width: 190,
			sorter: true,
			render: (v) => (v ? formatDateString(v) : "--"),
		},
		{
			title: trans("Current Assignee"),
			key: "currentAssignee",
			width: 150,
			render: (_, record) =>
				record.currentAssignee?.name || trans("Unassigned"),
		},
	];

	return (
		<Modal
			title={trans("New Dispatch Plan")}
			open={open}
			onCancel={onClose}
			width={800}
			centered
			maskClosable={false}
			styles={{
				body: { maxHeight: "70vh", overflowY: "auto", paddingRight: 4 },
			}}
			footer={[
				<Button key="cancel" onClick={onClose} disabled={creating}>
					{trans("Cancel")}
				</Button>,
				<Button
					key="create"
					type="primary"
					onClick={handleCreate}
					loading={creating}
				>
					{trans("Create Plan")}
				</Button>,
			]}
		>
			<p className="neutral-400 mb-3">
				{selectedIds.length} {trans("of")} {requests.length}{" "}
				{trans("selected requests are eligible")}
			</p>

			<p className="fs-16 fw-500 brand-700 text-capitalize mb-3">
				{trans("General Information")}
			</p>
			<div className="row">
				<div className="col-lg-6">
					<Input
						title={trans("Plan Name")}
						type="text"
						placeholder={trans("Ex. Plan-001")}
						formstyle="form-input"
						id="planName"
						name="planName"
						value={planName}
						onChange={(e) => setPlanName(e.target.value)}
						onBlur={() => setTouched((prev) => ({ ...prev, planName: true }))}
						errors={isPlanNameInvalid ? "Plan Name is required" : ""}
						touched={touched.planName}
					/>
				</div>
				<div className="col-lg-6">
					<InputSelect
						id="assigneeId"
						name="assigneeId"
						label={trans("Assignee To")}
						placeholder={trans("Select field staff")}
						options={assigneeOptions}
						loading={assigneesLoading}
						value={assigneeId}
						onChange={(val) => setAssigneeId(val)}
						onBlur={() => setTouched((prev) => ({ ...prev, assigneeId: true }))}
						errors={isAssigneeInvalid ? "Assignee is required" : ""}
						touched={touched.assigneeId}
						formstyle="form-input select-z-index"
						optionRender={(option) => (
							<div
								style={{ display: "flex", alignItems: "center", gap: "8px" }}
							>
								{option.data.avatar ? (
									<img
										src={option.data.avatar}
										alt={option.label}
										style={{ width: 24, height: 24, borderRadius: "50%" }}
									/>
								) : (
									<UserOutlined
										style={{ width: 24, height: 24, borderRadius: "50%" }}
									/>
								)}
								<span>{option.label}</span>
							</div>
						)}
					/>
				</div>
				<div className="col-lg-6">
					<InputDate
						title={trans("Planning Date")}
						id="planningDate"
						name="planningDate"
						onChange={(value) =>
							setPlanningDate(value ? dayjs(value).toISOString() : "")
						}
						onBlur={() =>
							setTouched((prev) => ({ ...prev, planningDate: true }))
						}
						value={planningDate || ""}
						errors={isPlanningDateInvalid ? "Planning Date is required" : ""}
						touched={touched.planningDate}
					/>
				</div>
			</div>

			<div className="d-flex align-items-center justify-content-between mb-3 mt-2">
				<p className="fs-16 fw-500 brand-700 text-capitalize mb-0">
					{trans("All Requests")}
				</p>
				<AntdInput
					prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
					placeholder={trans("Search")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{ width: 220 }}
					allowClear
				/>
			</div>
			<Table
				rowKey="id"
				loading={requestsLoading}
				columns={columns}
				dataSource={requests}
				pagination={false}
				scroll={{ x: "max-content", y: 280 }}
				onChange={handleTableChange}
				rowSelection={{
					selectedRowKeys: selectedIds,
					onChange: (keys) => setSelectedIds(keys),
				}}
			/>
		</Modal>
	);
}
